"use client";

import { useState } from "react";
import StatusBadge from "../atoms/StatusBadge";
import TextLink from "../atoms/TextLink";
import MemberQRCode from "../molecules/MemberQRCode";
import MemberDetailModal from "../molecules/MemberDetailModal";
import { event } from "@/lib/analytics/gtag";
import type { MembershipStatus } from "../types";
import { ALTRU_MEMBERSHIP_URL } from "@/lib/altru/urls";
import { ArrowUpRight } from "lucide-react";

interface MemberCardProps {
  memberId: string;
  memberName: string;
  memberSince?: string;
  expires?: string;
  isPrimary: boolean;
  membershipType: string;
  membershipStatus: MembershipStatus;
}

/**
 * @component MembershipCard
 * @description Membership card with two layouts:
 * - **Primary** (`isPrimary=true`): Full card — shows membership icon, member name, since/expires dates, ID, and QR code.
 * - **Secondary** (`isPrimary=false`): Compact card — shows membership icon, name, ID, and QR only.
 * Clicking either layout opens MemberDetailModal with full details.
 * When expired, card content is visually dimmed and a "Renew Membership" link appears.
 * @param memberId - Unique member identifier used for QR code generation.
 * @param memberName - Full name of the member.
 * @param memberSince - Year the member joined (e.g. "2020"). Primary only.
 * @param expires - Human-readable expiration date (e.g. "May 5, 2026"). Primary only.
 * @param isPrimary - True for the primary member card, false for household members.
 * @param membershipType - Label for the membership tier.
 * @param membershipStatus - Current membership status.
 * @returns An interactive card that opens a detail modal on click.
 */
export default function MembershipCard({
  memberId,
  memberName,
  memberSince,
  expires,
  isPrimary,
  membershipType,
  membershipStatus,
}: MemberCardProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const isExpired = membershipStatus === "EXPIRED";
  const handleOpen = () => {
    if (isExpired) return;
    setModalOpen(true);
    event("membership_card_opened", {
      category: "membership",
      label: memberName ?? undefined,
      membership_type: membershipType,
      membership_status: membershipStatus,
      is_primary: isPrimary ? "true" : "false",
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOpen();
    }
  };

  // Reconstruct MembershipMember shape for modal compatibility
  const member = {
    name: memberName,
    id: memberId,
    memberSince,
    expires,
    isPrimary,
  };

  return (
    <>
      <div
        className={`bg-white relative w-full h-full p-4 flex flex-col items-start justify-between transition-colors duration-200 group ${
          isPrimary ? "gap-6" : "gap-3"
        } ${isExpired ? "cursor-default" : "hover:bg-hover cursor-pointer"}`}
        role={isExpired ? undefined : "button"}
        tabIndex={isExpired ? undefined : 0}
        aria-label={`${memberName} membership card`}
        aria-haspopup={isExpired ? undefined : "dialog"}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
      >
        {/* Border + offset shadow */}
        <div
          aria-hidden="true"
          className="absolute inset-0 border border-border-light pointer-events-none shadow-l1 group-hover:shadow-l2 transition-shadow duration-200"
        />

        {/* Top section — name + status badge (primary only) */}
        <div className="flex flex-col gap-1.5 w-full relative">
          {isPrimary && (
            <div className="flex items-center justify-between">
              <p className="text-body-base text-charcoal inline-block">
                {membershipType}
              </p>
              <StatusBadge status={membershipStatus} />
            </div>
          )}
          <h3 className="text-title-body text-charcoal capitalize">
            {memberName}
          </h3>
        </div>

        {/* Bottom section — details + QR */}
        <div className="flex items-end justify-between w-full relative gap-0.5">
          {isPrimary ? (
            <div className="flex flex-col gap-0.5">
              {memberSince && (
                <p className={`text-body-base text-muted-text`}>
                  Member Since {memberSince}
                </p>
              )}
              {expires &&
                (isExpired ? (
                  <p className="text-labels-captions-bold text-white bg-near-black px-3 py-2">
                    Expired {expires}
                  </p>
                ) : (
                  <p className="text-body-base">
                    <span className="text-muted-text">Expires </span>
                    <span className="text-near-black">{expires}</span>
                  </p>
                ))}
              <p
                className={`text-body-base text-muted-text mt-1 ${isExpired ? "line-through" : ""}`}
              >
                ID: {memberId}
              </p>
            </div>
          ) : (
            <p className="text-body-base text-muted-text">ID: {memberId}</p>
          )}
          {!isExpired && (
            <div className="relative shrink-0">
              <MemberQRCode memberId={memberId} />
            </div>
          )}
        </div>

        {/* Renew link — primary expired only */}
        {isPrimary && isExpired && (
          <TextLink
            as="a"
            href={ALTRU_MEMBERSHIP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="relative"
            size="caption"
            onClick={(e) => {
              e.stopPropagation();
              event("membership_renew_clicked", {
                category: "membership",
                label: memberName ?? undefined,
                membership_type: membershipType,
                membership_status: membershipStatus,
                is_primary: isPrimary ? "true" : "false",
                source: "card",
              });
            }}
          >
            Renew Membership
            <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
          </TextLink>
        )}
      </div>

      <MemberDetailModal
        member={member}
        membershipType={membershipType}
        membershipStatus={membershipStatus}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </>
  );
}
